import React from "react";
import DeckGL from "@deck.gl/react";
import { WebMercatorViewport } from "@deck.gl/core";
import { TileLayer } from "@deck.gl/geo-layers";
import { BitmapLayer, PolygonLayer } from "@deck.gl/layers";
import { ArrowUpRight, CalendarDays, MapPinned, Route, School, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { arendalDaily, arendalFacts, arendalHourly } from "../../../data/arendalsuka";
import { may17SyntheticZones } from "../../../data/may17Zones";
import "../../../styles/home-projects.css";

/* 17 May: the same synthetic hexagon grid and shades as the case study, at its
   busiest stage. None of it is measured. */
const may17Shades = [
  [212, 239, 225],
  [143, 214, 181],
  [59, 181, 138],
  [14, 122, 87],
];

const hexagon = ([lng, lat], radius) => {
  const mPerDegLng = 111320 * Math.cos((lat * Math.PI) / 180);
  return Array.from({ length: 6 }, (_, k) => {
    const angle = Math.PI / 6 + (k * Math.PI) / 3;
    return [lng + (radius * Math.cos(angle)) / mPerDegLng, lat + (radius * Math.sin(angle)) / 111200];
  });
};

const may17Zones = may17SyntheticZones.map(zone => ({
  polygon: hexagon(zone.position, zone.radius),
  step: Math.min(4, Math.max(1, Math.round(zone.level * 4))),
}));

// Fitted for the narrowest portrait frame (about 290 px wide), so wider frames show more margin.
const may17View = (() => {
  const corners = may17Zones.flatMap(zone => zone.polygon);
  const bounds = [
    [Math.min(...corners.map(c => c[0])), Math.min(...corners.map(c => c[1]))],
    [Math.max(...corners.map(c => c[0])), Math.max(...corners.map(c => c[1]))],
  ];
  const { longitude, latitude, zoom } = new WebMercatorViewport({ width: 290, height: 362 }).fitBounds(bounds, {
    padding: { top: 64, bottom: 30, left: 10, right: 10 },
  });
  return { longitude, latitude, zoom, pitch: 0, bearing: 0 };
})();

const osmTiles = id => new TileLayer({
  id,
  data: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
  minZoom: 0,
  maxZoom: 19,
  tileSize: 256,
  renderSubLayers: props => {
    const { west, south, east, north } = props.tile.bbox;
    return new BitmapLayer({ id: props.id, image: props.data, bounds: [west, south, east, north] });
  },
});

/* ---------- Arendalsuka figures, all drawn from src/data/arendalsuka.js ---------- */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const count = value => value.toLocaleString("en-GB");
const shortDate = iso => {
  const [, month, day] = iso.split("-");
  return `${Number(day)} ${MONTHS[Number(month) - 1]}`;
};
const year = arendalFacts.fest_from.slice(0, 4);
const [peakDay, peakTime] = arendalFacts.peak_when.split(", ");
const [peakWeekday, peakDate, peakMonth] = peakDay.split(" ");
const peakShort = `${peakWeekday.slice(0, 3)} ${peakDate} ${peakMonth.slice(0, 3)}, ${peakTime}`;

// Part days at either end of the series are left out, so every point is a full day's peak.
const fullDays = arendalDaily.filter(day => !day.partial);
const firstDay = fullDays[0].d;
const lastDay = fullDays[fullDays.length - 1].d;
const chartPeriod = `${shortDate(firstDay)}–${shortDate(lastDay)} ${year}`;
const LONG_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const festivalDates = (() => {
  const [, fromMonth, fromDay] = arendalFacts.fest_from.split("-");
  const [, toMonth, toDay] = arendalFacts.fest_to.split("-");
  const from = fromMonth === toMonth ? Number(fromDay) : `${Number(fromDay)} ${LONG_MONTHS[Number(fromMonth) - 1]}`;
  return `${from}–${Number(toDay)} ${LONG_MONTHS[Number(toMonth) - 1]} ${year}`;
})();

const curve = (() => {
  const left = 46;
  const right = 588;
  const top = 14;
  const bottom = 292;
  const yMax = 32000;
  const step = (right - left) / (fullDays.length - 1);
  const x = index => left + index * step;
  const y = value => bottom - (value / yMax) * (bottom - top);
  const points = fullDays.map((day, index) => [x(index), y(day.peak)]);
  const line = points.map(([px, py], index) => `${index ? "L" : "M"}${px.toFixed(1)} ${py.toFixed(1)}`).join(" ");
  const festIndexes = fullDays
    .map((day, index) => (day.d >= arendalFacts.fest_from && day.d <= arendalFacts.fest_to ? index : -1))
    .filter(index => index >= 0);
  const peakIndex = fullDays.findIndex(day => day.peak === arendalFacts.peak);
  return {
    left,
    right,
    bottom,
    ticks: [0, 10000, 20000, 30000].map(value => ({ value, y: y(value) })),
    line,
    area: `${line} L${x(fullDays.length - 1).toFixed(1)} ${bottom} L${left} ${bottom} Z`,
    band: {
      x: x(festIndexes[0]) - step / 2,
      width: (festIndexes[festIndexes.length - 1] - festIndexes[0] + 1) * step,
    },
    baselineY: y(arendalFacts.baseline),
    peak: { x: x(peakIndex), y: y(arendalFacts.peak) },
  };
})();

const hours = (() => {
  const left = 4;
  const right = 236;
  const top = 6;
  const bottom = 82;
  const yMax = Math.max(...arendalHourly.map(hour => Math.max(hour.fest, hour.norm))) * 1.05;
  const slot = (right - left) / arendalHourly.length;
  const y = value => bottom - (value / yMax) * (bottom - top);
  return {
    bottom,
    slot,
    bars: arendalHourly.map((hour, index) => ({ h: hour.h, x: left + index * slot + slot * 0.18, y: y(hour.fest) })),
    normal: arendalHourly.map((hour, index) => `${(left + (index + 0.5) * slot).toFixed(1)},${y(hour.norm).toFixed(1)}`).join(" "),
    labelX: h => left + (h + 0.5) * slot,
  };
})();

const arendalDescription = `Chart of estimated devices in a ${arendalFacts.area_km2} km² area of Arendal sentrum. Daily peaks from ${shortDate(firstDay)} to ${shortDate(lastDay)} ${year} rise to ${count(arendalFacts.peak)} at ${peakTime} on ${peakDay} during Arendalsuka, ${arendalFacts.lift} times the July median of ${count(arendalFacts.baseline)}. Side panels compare average presence by hour in the festival week and on other days, and show foreign SIM cards at ${arendalFacts.foreign_fest_pct}% of devices in the festival week against ${arendalFacts.foreign_base_pct}% on other days.`;

/* ---------- Shared frame ---------- */

// A plain figure card: a title bar with an honest data label, then the figure.
// It is one image for assistive technology, described by `description`.
const FigureCard = ({ title, label, description, variant, footer, children }) => (
  <div className={`project-figure${variant ? ` project-figure--${variant}` : ""}`} role="img" aria-label={description}>
    <div className="project-figure__bar">
      <span>{title}</span>
      <small><i />{label}</small>
    </div>
    <div className="project-figure__screen">{children}</div>
    {footer}
  </div>
);

const ArendalVisual = () => (
  <FigureCard title={`Arendalsuka ${year}`} label="Estimated devices" description={arendalDescription}>
    <div className="arendal-dashboard">
      <div className="arendal-dashboard__kpis">
        <div><small>Peak</small><strong>{count(arendalFacts.peak)}</strong><span>{peakShort}</span></div>
        <div><small>Vs median</small><strong>{arendalFacts.lift}×</strong><span>July median {count(arendalFacts.baseline)}</span></div>
        <div><small>Readings</small><strong>10 min</strong><span>{arendalFacts.area_km2} km² centre</span></div>
      </div>
      <div className="arendal-dashboard__grid">
        <div className="arendal-dashboard__panel arendal-dashboard__curve">
          <div className="arendal-dashboard__panel-title"><span>Daily peak</span><small>{chartPeriod}</small></div>
          <svg viewBox="0 0 600 322" aria-hidden="true">
            <defs>
              <linearGradient id="home-arendal-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#4CAF50" stopOpacity="0.3" />
                <stop offset="1" stopColor="#4CAF50" stopOpacity="0" />
              </linearGradient>
            </defs>
            <rect x={curve.band.x} y="8" width={curve.band.width} height={curve.bottom - 8} className="project-chart-band" />
            {curve.ticks.map(({ value, y }) => (
              <g key={value}>
                <line x1={curve.left} y1={y} x2={curve.right} y2={y} className="project-chart-grid" />
                <text x={curve.left - 8} y={y + 4} textAnchor="end" className="project-chart-tick">{value ? `${value / 1000}k` : "0"}</text>
              </g>
            ))}
            <path d={curve.area} fill="url(#home-arendal-fill)" />
            <line x1={curve.left} y1={curve.baselineY} x2={curve.right} y2={curve.baselineY} className="project-chart-baseline" />
            <text x={curve.left + 6} y={curve.baselineY + 15} className="project-chart-note">July median {count(arendalFacts.baseline)}</text>
            <path d={curve.line} className="project-chart-line" />
            <circle cx={curve.peak.x} cy={curve.peak.y} r="6" className="project-chart-dot" />
            <text x={curve.peak.x - 12} y={curve.peak.y + 5} textAnchor="end" className="project-chart-value">{count(arendalFacts.peak)}</text>
            <text x={curve.band.x + curve.band.width / 2} y={curve.bottom - 8} textAnchor="middle" className="project-chart-band-label">Arendalsuka</text>
            <text x={curve.left} y="316" className="project-chart-tick">{shortDate(firstDay)}</text>
            <text x={curve.right} y="316" textAnchor="end" className="project-chart-tick">{shortDate(lastDay)}</text>
          </svg>
        </div>
        <div className="arendal-dashboard__side">
          <div className="arendal-dashboard__panel arendal-dashboard__hours">
            <div><span>Average by hour</span></div>
            <svg viewBox="0 0 240 96" aria-hidden="true">
              {hours.bars.map(bar => (
                <rect key={bar.h} x={bar.x} y={bar.y} width={hours.slot * 0.64} height={hours.bottom - bar.y} rx="1.5" className="project-hours-bar" />
              ))}
              <polyline points={hours.normal} className="project-hours-normal" />
              {[0, 6, 12, 18].map(h => (
                <text key={h} x={hours.labelX(h)} y="95" textAnchor="middle" className="project-chart-tick">{String(h).padStart(2, "0")}</text>
              ))}
            </svg>
            <p><span><em />Festival week</span><span><em className="is-normal" />Other days</span></p>
          </div>
          <div className="arendal-dashboard__panel arendal-dashboard__sim">
            <div><span>Foreign SIM cards</span></div>
            <p>
              <span>Festival week</span>
              <b><i style={{ width: `${(arendalFacts.foreign_fest_pct / arendalFacts.foreign_base_pct) * 100}%` }} /></b>
              <em>{arendalFacts.foreign_fest_pct}%</em>
            </p>
            <p>
              <span>Other days</span>
              <b><i className="is-normal" style={{ width: "100%" }} /></b>
              <em>{arendalFacts.foreign_base_pct}%</em>
            </p>
          </div>
        </div>
      </div>
    </div>
  </FigureCard>
);

const May17Visual = () => (
  <FigureCard
    variant="portrait"
    title="Central Oslo, 17 May"
    label="Illustrative"
    description={`Synthetic map of central Oslo divided into ${may17Zones.length} hexagonal zones, shaded by hand-set relative activity that is darkest in the middle. No measurements from 17 May are shown.`}
  >
    <div className="project-map-canvas">
      <DeckGL
        initialViewState={may17View}
        controller={false}
        layers={[
          osmTiles("home-may17-osm"),
          new PolygonLayer({
            id: "home-may17-zones",
            data: may17Zones,
            getPolygon: d => d.polygon,
            getFillColor: d => [...may17Shades[d.step - 1], 210],
            getLineColor: [14, 122, 87, 150],
            lineWidthMinPixels: 1,
            filled: true,
            stroked: true,
            pickable: false,
          }),
        ]}
      />
    </div>
    <div className="project-map-pill"><i />Relative activity <b>Synthetic data</b></div>
    <span className="project-map-credit">© OpenStreetMap contributors</span>
  </FigureCard>
);

const VaulenVisual = () => (
  <FigureCard
    variant="vaulen"
    title="Vaulen School move"
    label="Modelled"
    description="Map of Vaulen and Sørmarka in Stavanger marking Vaulen School, the interim school at Sørmarka and the crossing on Sørmarkveien used by 386 of the 750 modelled pupil routes. The modelled average walk rises from 9 to 31 minutes."
  >
    <svg className="project-vaulen-map" viewBox="0 0 891 686" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <image href="/vaulen-basemap.jpg" x="0" y="0" width="891" height="686" />
      <line x1="432" y1="416" x2="310" y2="226" className="project-vaulen-move" />
      <circle cx="360" cy="175" r="26" className="project-vaulen-crossing" />
      <circle cx="432" cy="416" r="13" className="project-vaulen-school" />
      <circle cx="310" cy="226" r="13" className="project-vaulen-school project-vaulen-school--interim" />
      <text x="454" y="424" className="project-vaulen-label">Vaulen School</text>
      <text x="288" y="262" textAnchor="end" className="project-vaulen-label">Sørmarka (interim)</text>
      <text x="394" y="166" className="project-vaulen-label project-vaulen-label--strong">386 routes cross here</text>
    </svg>
    <div className="project-vaulen-stat"><small>Average walk, modelled</small><strong>9 → 31 min</strong></div>
    <span className="project-map-credit project-map-credit--left">© OpenStreetMap contributors</span>
  </FigureCard>
);

const FrognVisual = () => (
  <FigureCard
    variant="frogn"
    title="Candidate grocery sites"
    label="Modelled"
    description="Map of candidate grocery-store sites across populated Frogn, shaded by modelled net spending kept in Frogn. The best candidate site would keep 29 MNOK a year."
  >
    <img src="/frogn/candidate-sites.jpg" alt="" />
    <div className="project-frogn-stat"><small>Best candidate site</small><strong>29 MNOK a year kept in Frogn</strong></div>
    <span className="project-map-credit">© CARTO © OpenStreetMap contributors</span>
  </FigureCard>
);

const FornebuVisual = () => (
  <FigureCard
    variant="fornebu"
    title="E18 closure forecast"
    label="Forecast"
    description="Frame from our forecast animation at Saturday 18:00 of the closure: the forecast tailback on the Oslo-bound E18 runs back from Fornebukrysset towards Sandvika. Forecast made before the closure: a peak queue of 4,675 vehicles, 165 minutes of added delay at the peak and 8,970 vehicles diverting. The closure ran from Friday 31 July at 21:00 to Monday 3 August 2026 at 06:00."
    footer={(
      <div className="project-fornebu-summary">
        <small>Forecast made before the closure <b>Fri 31 Jul 21:00 to Mon 3 Aug 06:00</b></small>
        <div><strong>4,675</strong><span>vehicles in the peak queue</span></div>
        <div><strong>165 min</strong><span>added delay at the peak</span></div>
        <div><strong>8,970</strong><span>vehicles forecast to divert</span></div>
      </div>
    )}
  >
    <img src="/fornebu/forecast-still.jpg" alt="" />
    <span className="project-map-credit">Map data © OpenStreetMap contributors</span>
  </FigureCard>
);

const ProjectCopy = ({ icon: Icon, category, title, body, fact }) => (
  <div className="home-project-copy">
    <div className="home-project-category"><Icon size={15} aria-hidden="true" />{category}</div>
    <h3>{title}</h3>
    <p>{body}</p>
    <div className="home-project-foot"><strong>{fact}</strong><span>Read case study <ArrowUpRight size={15} aria-hidden="true" /></span></div>
  </div>
);

const UseCasesSection = () => (
  <section id="use-cases" className="home-projects">
    <div className="home-projects__inner">
      <header className="home-projects__header">
        <div>
          <p className="home-projects__eyebrow">Selected projects</p>
          <h2>Five projects in Norway, from a festival week to an E18 lane closure.</h2>
        </div>
        <div>
          <p>We measure presence from aggregated mobile-network data and model what it cannot cover.</p>
          <Link to="/projects">View all projects <ArrowUpRight size={16} aria-hidden="true" /></Link>
        </div>
      </header>

      <div className="home-projects__grid">
        <Link to="/projects/arendalsuka" className="home-project-card home-project-card--feature">
          <div className="home-project-visual home-project-visual--feature"><ArendalVisual /></div>
          <ProjectCopy
            icon={CalendarDays}
            category="Event mobility · Arendal"
            title={`During Arendalsuka, presence in Arendal's town centre peaked at ${arendalFacts.lift} times the July median.`}
            body={`We measured presence every 10 minutes in a ${arendalFacts.area_km2} km² area of Arendal sentrum. At ${peakTime} on ${peakDay} ${year} it reached ${count(arendalFacts.peak)} estimated devices, against a July median of ${count(arendalFacts.baseline)}.`}
            fact={`Festival ${festivalDates}`}
          />
        </Link>

        <Link to="/projects/may-17th" className="home-project-card">
          <div className="home-project-visual home-project-visual--tablet"><May17Visual /></div>
          <ProjectCopy
            icon={ShieldCheck}
            category="Event readiness · Oslo"
            title="How central Oslo fills and empties on 17 May."
            body="The children's parade, street closures and visitors change how central Oslo works on Constitution Day. We show how busy each broad zone of the centre is as the day goes on, using aggregated mobile-network data. The map on this card is synthetic."
            fact="Quiet · Building · Busy · Easing"
          />
        </Link>

        <Link to="/projects/vaulen-school" className="home-project-card">
          <div className="home-project-visual"><VaulenVisual /></div>
          <ProjectCopy
            icon={School}
            category="School mobility · Stavanger"
            title="An interim move to Sørmarka would more than triple Vaulen pupils' average walk."
            body="We routed 750 modelled pupils to both sites on the footpath network. The average walk would go from 9 to 31 minutes, 442 pupils would walk more than 2 km, and 386 of the routes would cross Sørmarkveien at the same point."
            fact="750 pupils modelled"
          />
        </Link>

        <Link to="/projects/frogn-city-development" className="home-project-card home-project-card--wide">
          <ProjectCopy
            icon={MapPinned}
            category="Municipal planning · Frogn"
            title="Scoring sites for a new grocery store in Frogn."
            body="We tested a new store on each point of an 800 m grid across populated Frogn. In our retail model, the best site keeps 29 MNOK a year more grocery spending in Frogn after losses at existing stores. We also measured presence in two Drøbak zones through July 2026 and calibrated a road model to the three counters on the roads into Drøbak."
            fact="Measured + modelled + open data"
          />
          <div className="home-project-visual home-project-visual--frogn"><FrognVisual /></div>
        </Link>

        <Link to="/projects/road-closure-fornebu" className="home-project-card home-project-card--fornebu">
          <div className="home-project-visual home-project-visual--fornebu"><FornebuVisual /></div>
          <ProjectCopy
            icon={Route}
            category="Road impact forecast · Fornebu"
            title="Forecasting the effect of an E18 lane closure."
            body="Before the Oslo-bound E18 at Fornebukrysset was cut to one lane on 31 July 2026, we forecast the peak queue, the added delay and how many drivers would divert."
            fact="Closure 31 July–3 August 2026"
          />
        </Link>
      </div>
    </div>
  </section>
);

export default UseCasesSection;
